import type { paths } from "@/api-schema";
import { encodeLogoRequest } from "@/common/helpers/encode-file";
import { alphartisApiAxios } from "@/config/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const COMPANY_UPDATE_RESOURCE = "company" as const;
export const COMPANY_UPDATE_API_PATH = `/v1/${COMPANY_UPDATE_RESOURCE}/{uuid}` as const;

export type CompanyUpdateBody =
  paths[typeof COMPANY_UPDATE_API_PATH]["patch"]["requestBody"]["content"]["application/json"];
export type CompanyUpdateInput = Omit<CompanyUpdateBody, "logo"> & {
  uuid: string;
  logo?: File | null;
};
export type CompanyUpdateResponse =
  paths[typeof COMPANY_UPDATE_API_PATH]["patch"]["responses"][200]["content"]["application/json"];

export const useCompanyUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<CompanyUpdateResponse, Error, CompanyUpdateInput>({
    mutationFn: async (body: CompanyUpdateInput) => {
      const { uuid, ...rest } = body;
      const response = await alphartisApiAxios.patch<CompanyUpdateResponse>(
        `/v1/${COMPANY_UPDATE_RESOURCE}/${uuid}`,
        await encodeLogoRequest(rest),
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [COMPANY_UPDATE_RESOURCE],
      });
    },
  });
};
